// Cookie / analytics consent gating.
//
// Essential session cookies and localStorage (login session, theme preference)
// are required for the site to function and are never gated. Non-essential
// trackers — Google Analytics (gtag.js) and the Sentry Session Replay
// integration — only load after the visitor has accepted analytics. The choice
// is persisted under a versioned key so returning visitors are honored without
// re-prompting.

const CONSENT_KEY = "gbz_cookie_consent_v1";

export type ConsentValue = "accepted" | "declined";

const GA_MEASUREMENT_ID = "G-WXC7X59JKL";

type GtagWindow = Window & {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
};

let gaLoaded = false;
let sentryLoaded = false;

export function getStoredConsent(): ConsentValue | null {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === "accepted" || v === "declined" ? v : null;
  } catch {
    return null;
  }
}

export function storeConsent(value: ConsentValue): void {
  try {
    localStorage.setItem(CONSENT_KEY, value);
  } catch {
    // Storage may be unavailable (private mode / blocked); the choice simply
    // won't persist, which is acceptable — the banner re-prompts next visit.
  }
}

// Inject gtag.js and run the standard config. Mirrors what used to live in the
// static <head> of index.html, minus the auto page_view (the SPA's
// AnalyticsTracker emits every page_view, including the initial load).
export function loadGoogleAnalytics(): void {
  if (gaLoaded || typeof window === "undefined" || typeof document === "undefined") return;
  gaLoaded = true;

  const w = window as GtagWindow;
  w.dataLayer = w.dataLayer || [];

  // 🔴 Push `arguments`, NOT a rest-parameter array. gtag.js only executes
  // dataLayer entries that are `arguments` objects; a real Array is pushed,
  // ignored, and never runs.
  //
  // This file shipped the rest-parameter form on 2026-06-27 (8697fb82), when the
  // snippet moved out of the static <head> of index.html, and GA4 then recorded
  // NOTHING until 2026-09-07. The failure is silent in every direction that gets
  // checked: the container loads, `window.gtag` is a function, and every event
  // queues into dataLayer. Only the transport is missing.
  //
  // Measured on prod 2026-09-07 — same page, same container, same consent state:
  // the array form produced 0 requests to google-analytics.com and no `_ga`
  // cookie across ~20s and four events; the identical arguments pushed as an
  // `arguments` object produced two /g/collect hits carrying tid=G-WXC7X59JKL and
  // wrote `_ga` + `_ga_WXC7X59JKL` immediately.
  //
  // So do NOT "modernise" this back to `(...args) => push(args)`, and do NOT
  // satisfy the lint with `Array.from(arguments)`, `[...arguments]`, or
  // `slice.call(arguments)` — each of those reintroduces the outage verbatim.
  // It must stay a `function`: arrow functions have no `arguments`.
  // Fenced by analytics-consent.test.tsx, which asserts the pushed SHAPE.
  //
  // `_args` exists ONLY to give the call sites below a signature to typecheck
  // against — `function gtag()` with no parameters is a TS2554 at every call.
  // It is deliberately unread: a function with a rest parameter still receives a
  // full `arguments` object, and `arguments` is what must be pushed.
  function gtag(..._args: unknown[]) {
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer!.push(arguments);
  }
  w.gtag = gtag;
  gtag("js", new Date());
  gtag("config", GA_MEASUREMENT_ID, { send_page_view: false });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
}

// Initialize Sentry (error tracking, tracing, and the heavy Session Replay
// integration) by importing the instrument module lazily.
export function loadSentry(): void {
  if (sentryLoaded) return;
  sentryLoaded = true;
  void import("../instrument");
}

export function initConsentedTrackers(): void {
  loadGoogleAnalytics();
  loadSentry();
}
